import { Dataset, GridRow } from '../../../types/types';

export type RowsByMasterId = Map<string, GridRow[]>;

type LoadSubsetRowsFn = (params: {
  masterDataset: Dataset;
  masterRows: GridRow[];
  dataset: Dataset;
  activeSorting: { field: string; order: number }[];
  activeFilters: Record<string, unknown> | null;
  itemNames?: string[];
  includeIdItem: boolean;
  signal?: AbortSignal;
}) => Promise<GridRow[]>;

type Params = {
  masterDataset: Dataset | null;
  subsetDataset: Dataset;
  masterRows: GridRow[];
  itemNames: string[];
  activeSorting: { field: string; order: number }[];
  activeFilters: Record<string, unknown> | null;
  loadSubsetRows: LoadSubsetRowsFn;
  onMasterRowProcessed?: () => void;
  signal?: AbortSignal;
};

export const loadSubsetRowsByMaster = async ({
  masterDataset,
  subsetDataset,
  masterRows,
  itemNames,
  activeSorting,
  activeFilters,
  loadSubsetRows,
  onMasterRowProcessed,
  signal,
}: Params): Promise<RowsByMasterId> => {
  if (!masterDataset) {
    return new Map();
  }

  // A Map keeps data-driven ids like __proto__ as ordinary keys.
  const rowsByMasterId: RowsByMasterId = new Map();
  // Grid rows carry the synthetic _id, which is not a server column — only a real idItem can group.
  const masterIdItem: string | undefined = masterDataset.idItem;

  for (const masterRow of masterRows) {
    signal?.throwIfAborted();
    const masterId = masterIdItem ? String(masterRow[masterIdItem] ?? '') : '';

    if (!masterId) {
      rowsByMasterId.set(masterId, []);
      onMasterRowProcessed?.();
      continue;
    }

    // XML output needs child rows grouped under the exact parent master row.
    rowsByMasterId.set(
      masterId,
      await loadSubsetRows({
        masterDataset,
        masterRows: [masterRow],
        dataset: subsetDataset,
        activeSorting,
        activeFilters,
        itemNames,
        // Child rows are grouped by the master id, never by their own.
        includeIdItem: false,
        signal,
      })
    );
    onMasterRowProcessed?.();
  }

  return rowsByMasterId;
};
